import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { request } from '@/lib/http';
import { pagina, porCursor } from '@/lib/paginacao';
import {
  itemLoteSchema,
  type ListarLotesQuery,
  loteSchema,
  paginaNumeradaSchema,
  type ResultadoItemLote,
} from '@fiscoai/contracts';

import { type Envio } from './deteccao';

export function emAndamento(status: string | undefined): boolean {
  return status === 'RECEBIDO' || status === 'PROCESSANDO';
}

export function useEnviarLote() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (envio: Envio) => {
      const corpo = new FormData();
      // Planilha vai sozinha no campo `planilha`; documentos, um a um em `arquivos`.
      const campo = envio.tipo === 'PLANILHA' ? 'planilha' : 'arquivos';
      for (const arquivo of envio.arquivos) corpo.append(campo, arquivo);
      if (envio.empresaId) corpo.append('empresaId', envio.empresaId);
      return request('/importacoes', { method: 'POST', body: corpo, schema: loteSchema });
    },
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['importacoes'] }),
  });
}

export type PaginacaoLotes = Pick<
  ListarLotesQuery,
  'pagina' | 'porPagina' | 'ordenarPor' | 'ordem'
>;

/** Os mais recentes primeiro — padrão da lateral e da tela do histórico. */
export const LOTES_RECENTES: PaginacaoLotes = {
  pagina: 1,
  porPagina: 10,
  ordenarPor: 'criadoEm',
  ordem: 'desc',
};

/** Uma página do histórico; enquanto algum lote dela processa, se atualiza sozinha. */
export function useLotes(paginacao: PaginacaoLotes = LOTES_RECENTES) {
  return useQuery({
    queryKey: ['importacoes', 'lista', paginacao],
    queryFn: ({ signal }) =>
      request('/importacoes', {
        schema: paginaNumeradaSchema(loteSchema),
        query: paginacao,
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (consulta) =>
      consulta.state.data?.items.some((l) => emAndamento(l.status)) ? 2000 : false,
  });
}

/** Lote com polling de 1 s enquanto processa (docs/arquitetura/importacao.md). */
export function useLote(id: string) {
  return useQuery({
    queryKey: ['importacoes', id],
    queryFn: ({ signal }) => request(`/importacoes/${id}`, { schema: loteSchema, signal }),
    refetchInterval: (consulta) => (emAndamento(consulta.state.data?.status) ? 1000 : false),
  });
}

export function useItensLote(
  id: string,
  resultado: ResultadoItemLote | undefined,
  atualizar: boolean,
) {
  const consulta = useInfiniteQuery({
    queryKey: ['importacoes', id, 'itens', resultado],
    queryFn: ({ pageParam, signal }) =>
      request(`/importacoes/${id}/itens`, {
        schema: pagina(itemLoteSchema),
        query: { resultado, cursor: pageParam, limit: 200 },
        signal,
      }),
    ...porCursor,
    refetchInterval: atualizar ? 1500 : false,
  });
  // O server grava os itens em blocos: quando o lote termina, o último bloco pode ter chegado
  // depois da última consulta — busca uma vez mais ao sair de "em andamento".
  const estavaAtualizando = useRef(atualizar);
  const { refetch } = consulta;
  useEffect(() => {
    if (estavaAtualizando.current && !atualizar) void refetch();
    estavaAtualizando.current = atualizar;
  }, [atualizar, refetch]);
  return consulta;
}
